import React, { Fragment, useState, useEffect } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Button from '../../Button';
import EditBtn from '../../Button/EditBtn';
import { COMMON } from '../../../constants/strings';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import EmergencyContactFormSection from './EmergencyContactFormSection';
import toSnakeCaseObjectKeys from '../../../utils/toSnakeCaseObjectKeys';
import { EMERGENCY_CONTACT_DATA_KEY } from '../../../constants/metadata';
import { selectEditPatientErrors } from '../../../redux/profiles/selectors';
import { makeEmergencyInitialValue } from './utils/makeEmergencyInitialValue';
import { useEmergencyContactFormState } from './utils/useEmergencyContactFormState';
import { getFieldsEmergencyContactSection } from './utils/getFieldsEmergencyContactSection';

import styles from './styles.styl';

const EmergencyContact = ({
  profile,
  canEdit,
  savePatient,
  relationList,
  formatMessage,
  isSucceedUpdate,
}) => {
  const [isEdit, toggleEdit] = useState(false);

  const errors = useSelector(selectEditPatientErrors);

  const patientProfileId = get(profile, 'primaryPatientProfile.id');

  const fields = getFieldsEmergencyContactSection(formatMessage, profile);

  useEffect(() => {
    if (isEdit) toggleEdit(!isSucceedUpdate);
  }, [isSucceedUpdate]);

  const {
    formState,
    clearErrors,
    onEmailChange,
    validateErrors,
    handleFormChange,
    onMobilePhoneChange,
  } = useEmergencyContactFormState(
    makeEmergencyInitialValue(profile, relationList),
    errors,
  );

  const isEmailValid = !validateErrors.email;
  const relation = formState[PROFILE_FIELDS_TYPES.RELATION] || '';
  const phoneValue = formState[PROFILE_FIELDS_TYPES.PHONE_NUMBER] || '';

  const isPhoneValid =
    (phoneValue.length > 9 && !validateErrors.phoneNumber) ||
    !phoneValue.length;

  const handleSubmit = e => {
    e.preventDefault();

    clearErrors();

    savePatient({
      [EMERGENCY_CONTACT_DATA_KEY]: {
        ...toSnakeCaseObjectKeys(formState),
        relation: relation.value,
      },
      id: patientProfileId,
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>
          {formatMessage({ id: 'profile.section.emergencyContact' })}
        </h3>
        {canEdit && (
          <div className={styles.editButton}>
            <EditBtn
              onClick={() => {
                handleFormChange(
                  makeEmergencyInitialValue(profile, relationList),
                );
                toggleEdit(!isEdit);
              }}
            />
          </div>
        )}
      </div>
      {isEdit && (
        <EmergencyContactFormSection
          formState={formState}
          relationList={relationList}
          onChange={handleFormChange}
          onEmailChange={onEmailChange}
          formatMessage={formatMessage}
          validateErrors={validateErrors}
          onMobilePhoneChange={onMobilePhoneChange}
        />
      )}
      {!isEdit && (
        <div className={cn(styles.fieldsetContent, styles.withColumns)}>
          {fields &&
            fields.map(({ id, value, name }) => (
              <div className={styles.fieldWrapper} key={id}>
                <div className={styles.titleEntity}>{name}</div>
                <div className={styles.contentEntity}>{value || '-'}</div>
              </div>
            ))}
        </div>
      )}
      {isEdit && (
        <Fragment>
          <Button
            raised
            color="primary"
            onClick={handleSubmit}
            disabled={!isPhoneValid || !isEmailValid}
            className="mt3 mr2 user-profile-save-button"
          >
            <FormattedMessage {...COMMON.save} />
          </Button>
          <Button
            raised
            color="accent"
            className="mt3 user-profile-cancel-button"
            onClick={() => {
              clearErrors();
              handleFormChange(
                makeEmergencyInitialValue(profile, relationList),
              );
              toggleEdit(false);
            }}
          >
            <FormattedMessage {...COMMON.cancel} />
          </Button>
        </Fragment>
      )}
    </div>
  );
};

EmergencyContact.propTypes = {
  canEdit: PropTypes.bool,
  profile: PropTypes.object,
  savePatient: PropTypes.func,
  formatMessage: PropTypes.func,
  relationList: PropTypes.array,
  isSucceedUpdate: PropTypes.bool,
};

export default EmergencyContact;



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/EmergencyContact/index.jsx